import filter from 'lodash/filter';
import map from 'lodash/map';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import store from '@Store';
import allowedTags from '@Services/AllowedTag';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';

import * as Suggestions from '@Services/Suggestions';
import Catalyst from '@Services/Catalyst';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { keyify } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { TAXONOMY_BOUND_OPERATOR_DEFAULT } from './constants';

import {
  boundOperator as boundOperatorSelector,
  selectedItems as selectedItemsSelector,
  opModel as opModelSelector,
  suggestConfig as suggestConfigSelector,
} from './TaxonomyLookupModalSelectors';

import {
  taxonomyLookupModalSetIsLoadingReducerType,
  taxonomyLookupModalSetOpModelReducerType,
  taxonomyLookupModalSetSuggestConfigReducerType,
  taxonomyLookupModalSetBoundOperatorReducerType,
  taxonomyLookupModalSetSearchReducerType,
  taxonomyLookupModalSetSelectedItemsReducerType,
  taxonomyLookupModalSetTotalHitCountReducerType,
  taxonomyLookupModalSetTransferEnabledReducerType,
  typeOfTaxonomyLookupModal,
  NUM_OF_SUGGESTIONS,
} from './TaxonomyLookupModalConstants';

import { initialState } from './TaxonomyLookupModalReducer';

let searchHandler = null;
let resetHandler = null;

export const initTreeView = ({ search, reset }) => {
  searchHandler = search;
  resetHandler = reset;
};

export const resetTreeView = () => {
  searchHandler = null;
  resetHandler = null;
};

export const openTaxonomyLookupModal = (args) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfTaxonomyLookupModal,
      dialogType: typeOfTaxonomyLookupModal,
      movable: false,
      backdrop: true,
      ...args,
    })
  );
};

export const closeTaxonomyLookupModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfTaxonomyLookupModal,
    })
  );
};

export const dispatchOpenTaxonomyLookupModal = (...args) => {
  store.dispatch(openTaxonomyLookupModal(...args));
};

export const setIsLoading = (isLoading) => ({
  type: taxonomyLookupModalSetIsLoadingReducerType,
  payload: { isLoading },
});

export const setOpModel = (opModel) => ({
  type: taxonomyLookupModalSetOpModelReducerType,
  payload: { opModel },
});

export const setSuggestConfig = (suggestConfig) => ({
  type: taxonomyLookupModalSetSuggestConfigReducerType,
  payload: { suggestConfig },
});

export const setBoundOperator = (boundOperator) => ({
  type: taxonomyLookupModalSetBoundOperatorReducerType,
  payload: { boundOperator },
});

export const setSearch = (search) => ({
  type: taxonomyLookupModalSetSearchReducerType,
  payload: { search },
});

export const setSelectedItems = (selectedItems) => ({
  type: taxonomyLookupModalSetSelectedItemsReducerType,
  payload: { selectedItems },
});

export const setTotalHitCount = (totalHitCount) => ({
  type: taxonomyLookupModalSetTotalHitCountReducerType,
  payload: { totalHitCount },
});

export const setTransferEnabled = (transferEnabled) => ({
  type: taxonomyLookupModalSetTransferEnabledReducerType,
  payload: { transferEnabled },
});

export const resetState = () => (dispatch) => {
  dispatch(setIsLoading(initialState.get('isLoading')));
  dispatch(setOpModel(initialState.get('opModel')));
  dispatch(setSuggestConfig(initialState.get('suggestConfig')));
  dispatch(setSearch(initialState.get('search')));
  dispatch(setSelectedItems(initialState.get('selectedItems')));
  dispatch(setBoundOperator(initialState.get('boundOperator')));
  dispatch(setTotalHitCount(initialState.get('totalHitCount')));
  dispatch(setTransferEnabled(initialState.get('transferEnabled')));
};

export const applyResultsFromTreeView =
  ({ selectedTaxonomies, selectedCustom, totalHitCount }) =>
  async (dispatch, getState) => {
    const selectedItems = selectedItemsSelector(getState());
    const opModel = opModelSelector(getState());

    if (selectedTaxonomies) {
      dispatch(
        setOpModel({
          ...opModel,
          selection: selectedTaxonomies,
        })
      );
    }

    if (selectedCustom && !isEqual(selectedItems, selectedCustom)) {
      dispatch(setSelectedItems(selectedCustom));
      dispatch(setTransferEnabled(true));
    }

    if (!isUndefined(totalHitCount)) {
      dispatch(setTotalHitCount(totalHitCount));
    }
  };

export const applyChanges = (closeCallback) => async (dispatch, getState) => {
  const suggestConfig = suggestConfigSelector(getState());
  const opModel = opModelSelector(getState());
  const selectedItems = selectedItemsSelector(getState());

  Catalyst.trackEvent('buttonClick', {
    buttonType: `taxomony:selected:${suggestConfig.title}`,
  });

  dispatch(closeTaxonomyLookupModal());

  if (closeCallback) {
    closeCallback({
      selected: opModel.selection,
      selectedCustom: selectedItems,
    });
  }
};

export const clearSelected = () => async (dispatch, getState) => {
  const opModel = opModelSelector(getState());
  dispatch(
    setOpModel({
      ...opModel,
      selection: null,
    })
  );
  dispatch(setTotalHitCount(0));

  if (resetHandler) {
    resetHandler();
  }
};

export const getSuggestions = (stringToSearch) => async (dispatch, getState) => {
  const suggestConfig = suggestConfigSelector(getState());

  return Suggestions.getSuggestions({
    databaseId: suggestConfig.databaseId,
    matchesCount: NUM_OF_SUGGESTIONS,
    fieldIds: [suggestConfig.queryLet.id],
    expandedValue: stringToSearch,
    origin: 'LOOKUP_MODAL',
  })
    .then((response) => {
      const suggestions = response[0].sub;
      return map(suggestions, (suggestion) => {
        const title = allowedTags(suggestion.title);
        return {
          title,
          value: title,
        };
      }).slice(0, 5);
    })
    .catch(Logger.reject);
};

export const clearSearch = () => (dispatch) => {
  dispatch(setSearch(''));
};

export const searchByKeyword = (keyword) => async (dispatch, getState) => {
  const boundOperator = boundOperatorSelector(getState());
  dispatch(setSearch(keyword));

  const query = {
    text: keyword,
    boundOperator,
  };

  if (searchHandler) {
    searchHandler(query);
  }
};

export const showEmptyResult = () => () => {
  NotifyDialogsService.openModalWindow({
    title: 'No results',
    text: 'No terms matched your query were found.',
    confirmText: 'Close',
    type: 'info',
    backdrop: 'static',
    trackingOptions: {
      component: 'Taxonomy',
    },
  });
};

export const getTreeView = (params, suggestInit) => async () => {
  const { cache } = suggestInit;
  const key = keyify(params);
  if (cache[key]) {
    return Promise.resolve(cache[key]);
  }
  return MessageBroker.send(RequestUrls.taxonomy, params)
    .then((resp) => {
      cache[key] = resp;
      return resp;
    })
    .catch(Logger.reject);
};

export const init = (ownProps) => async (dispatch) => {
  const { suggestConfig, suggestInit } = ownProps;
  dispatch(resetState());
  dispatch(setIsLoading(true));
  dispatch(setSuggestConfig(suggestConfig));
  dispatch(
    setOpModel({
      title: suggestConfig.title,
      selection: [],
    })
  );

  const boundOperator = suggestConfig.queryLet.defaultBoundOperator || TAXONOMY_BOUND_OPERATOR_DEFAULT;
  dispatch(setBoundOperator(boundOperator));

  if (suggestInit.search && suggestInit.search.length >= 2) {
    const terms = suggestInit.search.includes(';') ? suggestInit.search.split(';') : [suggestInit.search];

    if (terms.length === 1) {
      dispatch(setSearch(suggestInit.search));
      // dispatch(searchByKeyword(suggestInit.search));
    } else if (terms.length > 1) {
      const selectedItems = filter(terms, (term) => term && !/^\s*$/gi.test(term));
      dispatch(setSelectedItems(selectedItems));
    }
  }
};
